import { useState, type ReactNode } from 'react';
import { T, useGT } from 'gt-react';
import { ArrowBendDownRightIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { PopularityCode } from 'src/components/procurement/PopularityCell';
import type {
  AssignmentLine,
  LineStockLocation,
} from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import { stockFlag } from '../utils/stockFlag';
import {
  filterStockLocations,
  normalizeStockSearch,
  stockLocationAction,
} from '../utils/stockElsewhere';
import { NO_FACT } from '../utils/lineFacts';

function quantity(value: number | null): string {
  return value === null ? NO_FACT : Math.round(value).toLocaleString();
}

function Num({
  value,
  strong = false,
}: {
  value: number | null;
  strong?: boolean;
}) {
  return (
    <td
      className={cn(
        'px-2 py-1 text-right tabular-nums',
        strong && 'font-semibold',
        value !== null && value < 0 && 'text-red-600',
      )}
    >
      {quantity(value)}
    </td>
  );
}

function Head({
  children,
  numeric = true,
}: {
  children: ReactNode;
  numeric?: boolean;
}) {
  return (
    <th
      className={cn(
        'whitespace-nowrap px-2 py-1 font-medium',
        numeric && 'text-right',
      )}
    >
      {children}
    </th>
  );
}

/**
 * The short division code, so same-named locations read apart (two Ottawas:
 * W+ and QO). The division's name is on hover.
 */
function DivisionCell({ location }: { location: LineStockLocation }) {
  return (
    <td className="px-2 py-1" title={location.divisionName ?? ''}>
      <span className="font-mono font-medium">
        {location.divisionCode ?? location.divisionName ?? NO_FACT}
      </span>
    </td>
  );
}

function LocationCell({ location }: { location: LineStockLocation }) {
  return (
    <td className="px-2 py-1">
      <div className="flex items-center gap-1 whitespace-nowrap">
        <span className="font-mono font-medium">
          {location.warehouseCode ?? location.warehouseName}
        </span>
        {location.isDestination && (
          <span className="rounded bg-blue-200 px-1 text-[10px] uppercase tracking-wide text-blue-900">
            <T context="destination warehouse marker">dest</T>
          </span>
        )}
        {location.type === 'hub' && (
          <span className="rounded bg-gray-200 px-1 text-[10px] uppercase tracking-wide text-gray-700">
            <T context="hub warehouse marker">hub</T>
          </span>
        )}
      </div>
      {location.warehouseCode !== null && (
        <div className="whitespace-nowrap text-[10px] text-muted-foreground">
          {location.warehouseName}
        </div>
      )}
    </td>
  );
}

function ActionCell({
  location,
  line,
  pending,
  onTransfer,
}: {
  location: LineStockLocation;
  line: AssignmentLine | null;
  pending: boolean;
  onTransfer: (warehouseId: string) => void;
}) {
  const action = stockLocationAction(location, line);
  return (
    <td className="px-2 py-1 text-right">
      {action === 'current-source' && (
        <span className="whitespace-nowrap text-[10px] uppercase tracking-wide text-muted-foreground">
          <T context="stock elsewhere: this location is the line's transfer source">
            Source
          </T>
        </span>
      )}
      {action === 'transfer' && (
        <Button
          size="sm"
          variant="ghost"
          className="h-6 px-2 text-xs"
          disabled={pending}
          onClick={() => onTransfer(location.warehouseId)}
        >
          <ArrowBendDownRightIcon aria-hidden className="h-3.5 w-3.5" />
          <T context="stock elsewhere: transfer the line from this location">
            Transfer
          </T>
        </Button>
      )}
    </td>
  );
}

/**
 * The SKU at every location that holds or plans it, as the GAINS Order
 * Builder's SKU tab lists it, sorted by net available. A buyer can switch the
 * line to a transfer from any location with stock to spare. Every row names
 * its division, and a search box finds a location by code, name or division
 * across all divisions (GEN-3147).
 */
export function StockByLocationTable({
  locations,
  line,
  pending,
  onTransfer,
}: {
  locations: readonly LineStockLocation[];
  line: AssignmentLine | null;
  /** A source change is saving: transfer buttons wait. */
  pending: boolean;
  onTransfer: (warehouseId: string) => void;
}) {
  const gt = useGT();
  const [allDivisions, setAllDivisions] = useState(false);
  const [search, setSearch] = useState('');
  const rows = filterStockLocations(locations, { allDivisions, search });
  const searching = normalizeStockSearch(search) !== '';
  const nothingMatches = searching && rows.every((row) => row.isDestination);
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={gt('Search code, name or division', {
            context: 'stock elsewhere: location search placeholder',
          })}
          aria-label={gt('Search locations', {
            context: 'stock elsewhere: location search',
          })}
          className="h-7 w-56 text-xs"
        />
        <label
          className={cn(
            'flex items-center gap-2 text-xs text-muted-foreground',
            searching && 'opacity-50',
          )}
          title={
            searching
              ? gt('A search looks across all divisions', {
                  context: 'stock elsewhere: why the division toggle is off',
                })
              : undefined
          }
        >
          <Checkbox
            checked={allDivisions || searching}
            disabled={searching}
            onCheckedChange={(checked) => setAllDivisions(checked === true)}
            aria-label={gt('Show all divisions', {
              context: 'stock elsewhere: include other divisions toggle',
            })}
          />
          <T context="stock elsewhere: include other divisions toggle">
            All divisions
          </T>
        </label>
      </div>
      <div className="overflow-x-auto rounded border border-gray-200">
        <table className="w-full min-w-max text-xs">
          <thead className="bg-gray-50 text-left text-gray-600">
            <tr>
              <Head numeric={false}>
                <T context="stock elsewhere column: division short code">Div</T>
              </Head>
              <Head numeric={false}>
                <T context="stock elsewhere column: location">Location</T>
              </Head>
              <Head numeric={false}>
                <T context="stock elsewhere column: stocked (Y/N)">Stock</T>
              </Head>
              <Head numeric={false}>
                <T context="stock elsewhere column: popularity code">Pop</T>
              </Head>
              <Head>
                <T context="stock elsewhere column: AS400 min / max">
                  Min / Max
                </T>
              </Head>
              <Head>
                <T context="stock elsewhere column: on hand">On hand</T>
              </Head>
              <Head>
                <T context="stock elsewhere column: unshipped">Unshipped</T>
              </Head>
              <Head>
                <T context="stock elsewhere column: available">Available</T>
              </Head>
              <Head>
                <T context="stock elsewhere column: on order">On order</T>
              </Head>
              <Head>
                <T context="stock elsewhere column: inbound transfers">
                  Transfers in
                </T>
              </Head>
              <Head>
                <T context="stock elsewhere column: net available">Net avail</T>
              </Head>
              <Head>
                <T context="stock elsewhere column: sales last 12 months">
                  Sales 12 mo
                </T>
              </Head>
              <th className="px-2 py-1">
                <span className="sr-only">
                  <T context="stock by location table column, row actions">
                    Actions
                  </T>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((location) => (
              <tr
                key={location.warehouseId}
                className={
                  location.isDestination
                    ? 'border-t border-blue-200 bg-blue-50'
                    : 'border-t border-gray-100'
                }
              >
                <DivisionCell location={location} />
                <LocationCell location={location} />
                <td className="px-2 py-1" title={location.stockStatus ?? ''}>
                  {stockFlag(location.stockStatus) ?? NO_FACT}
                </td>
                <td className="px-2 py-1">
                  <PopularityCode code={location.popularityCode} />
                </td>
                <td className="whitespace-nowrap px-2 py-1 text-right tabular-nums">
                  {quantity(location.policyMin)} /{' '}
                  {quantity(location.policyMax)}
                </td>
                <Num value={location.onHand} />
                <Num value={location.unshipped} />
                <Num value={location.available} />
                <Num value={location.onOrder} />
                <Num value={location.transfersIn} />
                <Num value={location.netAvailable} strong />
                <Num value={location.sales12Months} />
                <ActionCell
                  location={location}
                  line={line}
                  pending={pending}
                  onTransfer={onTransfer}
                />
              </tr>
            ))}
            {nothingMatches && (
              <tr className="border-t border-gray-100" aria-hidden>
                <td
                  colSpan={13}
                  className="px-2 py-3 text-center text-muted-foreground"
                >
                  <T context="stock elsewhere: search found no other location">
                    No other location matches
                  </T>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {/* Mounted before a search so the empty result is announced while
          focus stays in the search box; the table row above is visual. */}
      <p role="status" className="sr-only">
        {nothingMatches && (
          <T context="stock elsewhere: search found no other location">
            No other location matches
          </T>
        )}
      </p>
    </div>
  );
}
